import { defineStore } from 'pinia';
import router from '@/router';
import { InstallationStatuses } from './installations';
import { computed, ref } from 'vue';
import { useApi } from '@/composables/useApi';
import { deepMerge, JSONFlatDotToNested } from '@/helpers/util';
import { useI18n } from 'vue-i18n';
import { useAppColors } from '@/composables/useAppColors';
import { getLocalstorageItem, setLocalstorageItem } from '@/helpers/localStorage';
import { insertGoogleTagManager, storeGoogleTag } from '@/helpers/googleTagManager';

export const useAppStore = defineStore('appStore', () => {
  const { updateColors } = useAppColors();
  const i18n = useI18n();
  const { t } = i18n;
  const { apiGet } = useApi();

  const pageLoading = ref(true);
  const config = ref({});
  const features = ref([]);
  const logo = ref(getLocalstorageItem('logo'));
  const fluviusDoc = ref();
  const kWhToKgCo2eByCountry = ref({});
  const validActions = ref([]);
  const socialReviewLinks = ref([]);

  const hasSocialReviewLinks = computed(() => socialReviewLinks.value?.length > 0);

  async function getActions(installation) {
    const installationTitleText = installation.title;
    const titleText = installationTitleText === '' ? '' : `  ${t('with')} ${installationTitleText}`;
    return [
      {
        name: 'signOffer',
        title: `${t('actions.signOfferTitle')}${titleText}`,
        buttonText: t('actions.signOfferButtonText'),
        priority: 3,
        action: () => {
          router.push(
            {
              name: installation.offerVersionNumbers.split(',').length > 1 ? 'offerDetailList' : 'offerDetail',
              params: installation.offerVersionNumbers.split(',').length > 1
                ? {
                    id: installation.offerId,
                  }
                : {
                    id: installation.offerId,
                    version: installation.offerVersionNumbers,
                  },
            });
        },
        condition: installation.status === InstallationStatuses.New && installation.offerId > 0,
      },
      {
        name: 'prepay',
        title: `${t('actions.payPrepaymentTitle')}${titleText}`,
        buttonText: t('actions.payPrepaymentButtonText'),
        priority: 2,
        action: () => {
          router.push({ name: 'orderDetail', params: { installation_id: installation.id, id: installation.orderId } });
        },
        condition: installation.status === InstallationStatuses.Needs_Prepayment,
      },
      {
        name: 'payfinal',
        title: `${t('actions.payFinalInvoiceTitle')}${titleText}`,
        buttonText: t('actions.payFinalInvoiceButtonText'),
        priority: 1,
        action: () => {
          router.push({
            name: 'invoiceDetail',
            params: { installation_id: installation.id, id: installation.finalInvoiceId },
          });
        },
        condition: installation.status === InstallationStatuses.Delivered && installation.finalInvoiceId,
      },
      {
        name: 'fillInSurvey',
        title: `${t('actions.surveyTitle')} ${installation.orderId}`,
        buttonText: t('actions.survey'),
        priority: 0,
        action: () => {
          router.push({
            name: 'installations_survey',
            params: { installation_id: installation.id, order_id: installation.orderId },
          });
        },
        condition: feature('survey') && (installation.finalInvoicePaid && !installation.surveyed),
      },
      {
        name: 'signIChoosr',
        title: `${t('actions.iChoosrTitle')} ${t('for')} ${t('orderDetail.order') + ' ' + installation.orderId}`,
        buttonText: t('actions.iChoosr'),
        priority: 0.5,
        action: () => {
          router.push({ name: 'ichoosr', params: { installation_id: installation.id, id: installation.orderId } });
        },
        condition: installation.hasExternalPrepayment && installation.qrFilledIn && !installation.opleveringDone,
      },
    ];
  }

  async function setDynamicCardActions(installations) {
    const dynamicActions = [];

    for (const installation of installations) {
      const actions = await getActions(installation);
      for (const action of actions) {
        if (action.condition) {
          dynamicActions.push(action);
        }
      }
    }

    if (dynamicActions.length === 0 && feature('loyalty')) {
      dynamicActions.push({
        name: 'loyalty',
        title: `${t('actions.loyaltyTitle')}`,
        buttonText: t('actions.loyalty'),
        priority: 0,
        action: () => {
          router.push({ name: 'loyalty' });
        },
        condition: true,
      });
    }

    // Sort actions by priority and pick the first
    dynamicActions.sort((a, b) => b.priority - a.priority);

    validActions.value = dynamicActions;
  }

  async function getAppConfig() {
    const appConfig = await apiGet('portal/config');

    if (appConfig.features) {
      updateSubdomainFeatures(appConfig.features);
    }

    if (appConfig.colors) {
      updateSubdomainColors(appConfig.colors);
    }

    if (appConfig.texts) {
      updateSubdomainTexts(appConfig.texts);
    }

    if (appConfig.logo) {
      updateSubdomainLogo(appConfig.logo);
    }

    socialReviewLinks.value = appConfig.socialReviewLinks;
    kWhToKgCo2eByCountry.value = appConfig.kWhToKgCo2eByCountry;
    config.value = appConfig.config;
    fluviusDoc.value = appConfig.fluviusDoc;

    insertGoogleTagManager(appConfig.tagId);
    storeGoogleTag(appConfig.tagId);
  }

  function updateSubdomainFeatures(subDomainFeatures) {
    features.value = [...subDomainFeatures];
  }

  function updateSubdomainTexts(texts) {
    updateSubdomainLocaleMessage('nl', texts.nl);
    updateSubdomainLocaleMessage('fr', texts.fr);
    updateSubdomainLocaleMessage('en', texts.en);
  }

  function updateSubdomainLocaleMessage(locale, messages) {
    i18n.setLocaleMessage(locale, deepMerge(
      i18n.getLocaleMessage(locale),
      JSONFlatDotToNested(messages),
    ));
  }

  function updateSubdomainColors(colors) {
    updateColors(colors);
  }

  function updateSubdomainLogo(newLogo) {
    logo.value = newLogo;
    setLocalstorageItem('logo', logo.value);
  }

  function getFromConfig(keyString, defaultValue) {
    if (!keyString) {
      return defaultValue;
    }

    const keys = keyString.split('.');

    return keys.reduce((acc, key) => acc?.[key], config.value) ?? defaultValue;
  }

  /**
   * Check if feature is available
   */
  function feature(feature) {
    return features.value.includes(feature);
  }

  return {
    logo,
    fluviusDoc,
    pageLoading,
    validActions,
    kWhToKgCo2eByCountry,
    socialReviewLinks,
    hasSocialReviewLinks,
    setDynamicCardActions,
    getAppConfig,
    getFromConfig,
    feature,
  };
});
